///
/// EarthBrowser
///
/// Created by Matt Giger on 9/18/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Client side of Web Push: service worker registration and
/// PushManager subscription against the app's VAPID public key. The
/// server registration (persisting the subscription for the alert
/// evaluator) lives in pushSubscriptionsApi; these helpers only talk to
/// the browser.
///

const cServiceWorkerUrl = "/push-sw.js"

export type PushSubscribeResult =
	| { status: "subscribed"; endpoint: string; p256dh: string; auth: string }
	| { status: "denied" }
	| { status: "unsupported" }

export function pushSupported(): boolean {
	return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window
}

export function pushPermission(): NotificationPermission | "unsupported" {
	return pushSupported() ? Notification.permission : "unsupported"
}

function vapidPublicKeyBytes(): Uint8Array {
	const b64 = (import.meta.env.VITE_VAPID_PUBLIC_KEY) ?? ""
	const normalized = b64.replace(/-/g, "+").replace(/_/g, "/")
	const padded = normalized + "=".repeat((4 - normalized.length % 4) % 4)
	return Uint8Array.from(atob(padded), c => c.charCodeAt(0))
}

async function serviceWorkerRegistration(): Promise<ServiceWorkerRegistration> {
	const registration = await navigator.serviceWorker.register(cServiceWorkerUrl)
	await navigator.serviceWorker.ready
	return registration
}

function subscriptionKeys(subscription: PushSubscription): { endpoint: string; p256dh: string; auth: string } | null {
	const json = subscription.toJSON()
	if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) return null
	return { endpoint: json.endpoint, p256dh: json.keys.p256dh, auth: json.keys.auth }
}

/**
 * Ensure this browser has a push subscription: request notification
 * permission (must be called from a user gesture) and subscribe. Safe to
 * call repeatedly — an existing subscription is returned as-is.
 */
export async function ensurePushSubscription(): Promise<PushSubscribeResult> {
	if (!pushSupported() || !import.meta.env.VITE_VAPID_PUBLIC_KEY) {
		return { status: "unsupported" }
	}

	const permission = await Notification.requestPermission()
	if (permission !== "granted") {
		return { status: "denied" }
	}

	let subscription: PushSubscription | null
	try {
		const registration = await serviceWorkerRegistration()
		subscription = await registration.pushManager.getSubscription()
		if (!subscription) {
			subscription = await registration.pushManager.subscribe({
				userVisibleOnly: true,
				applicationServerKey: vapidPublicKeyBytes().buffer as ArrayBuffer,
			})
		}
	} catch (error) {
		// Browsers without a push service (some Electron shells, esoteric
		// builds) throw AbortError here despite advertising PushManager
		console.warn("[push] Subscribe failed:", error)
		return { status: "unsupported" }
	}

	const keys = subscriptionKeys(subscription)
	if (!keys) {
		return { status: "unsupported" }
	}
	return { status: "subscribed", ...keys }
}

/** This browser's current subscription endpoint, if any (no prompting) */
export async function currentPushEndpoint(): Promise<string | null> {
	if (!pushSupported()) return null
	const registration = await navigator.serviceWorker.getRegistration(cServiceWorkerUrl)
	if (!registration) return null
	const subscription = await registration.pushManager.getSubscription()
	return subscription?.endpoint ?? null
}

/** Unsubscribe this browser; returns the endpoint that was released */
export async function unsubscribePush(): Promise<string | null> {
	if (!pushSupported()) return null
	const registration = await navigator.serviceWorker.getRegistration(cServiceWorkerUrl)
	const subscription = await registration?.pushManager.getSubscription()
	if (!subscription) return null
	const endpoint = subscription.endpoint
	await subscription.unsubscribe()
	return endpoint
}
